import React, { useEffect, useRef, useState } from "react";
import MHeader from "@/components/ui/organisms/MHeader";
import { IMedication } from "@/types/medication";
import { ISupplement } from "@/types/supplement";
import { ListItem as MListItem } from "@/components/ui/atoms/MListItem";
import { getMemberInfo } from "@/hooks/useMembers";
import { useMemberMedications } from "@/hooks/member-medication.hook";
import { useMemberSupplements } from "@/hooks/member-supplement.hook";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MLoader from "@/components/ui/atoms/MLoader";
import MDialog from "@/components/ui/atoms/MDialog";
import EmptyAfterDeleteDialog from "@/components/ui/molecules/EmptyAfterDeleteDialog";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MTabs from "@/components/ui/molecules/MTabs";
import {
  IconEdit,
  IconCircleX,
  IconPillFilled,
  IconLeaf,
  IconPillOff,
  IconLeafOff,
} from "@tabler/icons-react";
import styles from "./MedicationForm.module.css";

import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ReminderFrequency } from "@/types/reminders";
import { useNavigationSource } from "@/hooks/useNavigationSource";

const Medications: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<"medicine" | "supplement">(
    "medicine"
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmptyDialog, setShowEmptyDialog] = useState(false);
  const [medicationToDelete, setMedicationToDelete] =
    useState<IMedication | null>(null);
  const [supplementToDelete, setSupplementToDelete] =
    useState<ISupplement | null>(null);
  // Tracks whether the user has observed at least one item during this visit,
  // so we only surface the "no items left" modal when they actually deleted
  // the last entry — not when they simply landed on an empty page.
  const hadItemsRef = useRef(false);
  // Ensures the "default to Supplements when no medicines exist" auto-switch
  // runs only once on first load — never overrides a manual tab change.
  const tabAutoSetRef = useRef(false);

  // Backend medications and supplements logic
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const {
    medications: backendMedications,
    loading: loadingBackendMeds,
    fetched: fetchedMeds,
    setFetched: setFetchedMeds,
    fetchMedications,
    deleteMedication,
  } = useMemberMedications(memberId);

  const {
    supplements: backendSupplements,
    loading: loadingBackendSupplements,
    fetched: fetchedSupplements,
    setFetched: setFetchedSupplements,
    fetchSupplements,
    deleteSupplement,
  } = useMemberSupplements(memberId);

  // Check URL parameters for tab state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab") as "medicine" | "supplement";
    if (tabParam === "medicine" || tabParam === "supplement") {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (memberId && !fetchedMeds) {
      fetchMedications();
    }
  }, [memberId, fetchedMeds, fetchMedications]);

  useEffect(() => {
    if (memberId && !fetchedSupplements) {
      fetchSupplements();
    }
  }, [memberId, fetchedSupplements, fetchSupplements]);

  useEffect(() => {
    if (!fetchedMeds || !fetchedSupplements) return;
    const totalItems =
      backendMedications.length + backendSupplements.length;
    if (totalItems > 0) {
      hadItemsRef.current = true;
      // On first load: if there are no medicines but supplements exist, land
      // on the Supplements tab so the user sees their data without an extra
      // tap. Respect any explicit ?tab=... URL override.
      if (!tabAutoSetRef.current) {
        tabAutoSetRef.current = true;
        const tabParam = new URLSearchParams(location.search).get("tab");
        if (
          tabParam !== "medicine" &&
          tabParam !== "supplement" &&
          backendMedications.length === 0 &&
          backendSupplements.length > 0
        ) {
          setActiveTab("supplement");
        }
      }
      return;
    }
    if (hadItemsRef.current) {
      // User just deleted the last item — prompt before leaving.
      setShowEmptyDialog(true);
    } else {
      // Landed on an already-empty page — preserve original redirect.
      const didNavigate = navigateBackIfExists();
      if (!didNavigate) {
        navigate("/profile/health-profile");
      }
    }
  }, [fetchedMeds, fetchedSupplements, backendMedications, backendSupplements]);

  // Edit handler
  const handleEdit = (_med: IMedication, idx: number) => {
    navigate(
      `/profile/health-profile/medication/medicationform?mode=edit&type=medicine&index=${idx}`
    );
  };

  // Delete handlers
  const handleDeleteMedication = async () => {
    try {
      if (!medicationToDelete) return;
      await deleteMedication(medicationToDelete.id!);
      setFetchedMeds(false);
      await fetchMedications();
    } catch {
      showToast({ message: t("errors.toast"), variant: "error" });
    }
    setShowDeleteModal(false);
    setMedicationToDelete(null);
  };

  const handleDeleteSupplement = async () => {
    try {
      if (!supplementToDelete) return;
      await deleteSupplement(supplementToDelete.id!);
      setFetchedSupplements(false);
      await fetchSupplements();
    } catch {
      showToast({ message: t("errors.toast"), variant: "error" });
    }
    setShowDeleteModal(false);
    setSupplementToDelete(null);
  };

  const getSecondaryText = (item: IMedication | ISupplement) => {
    if (!item.frequency) return t("medications.frequencyNotAdded");
    switch (item.frequency) {
      case ReminderFrequency.DAY_FREQUENCY:
        return t("medications.frequencyEveryXDays", {
          x: item.everyXDays,
        });
      case ReminderFrequency.SPECIFIC_DAYS:
        return t("medications.specificDays", {
          days:
            item.selectedWeekdays
              ?.map((day) => t(`reminders.weekDisplay.${day}`))
              .join(", ") || "",
        });
      default:
        return t(`reminders.frequency.${item.frequency}`);
    }
  };

  if (loadingBackendMeds || loadingBackendSupplements) {
    return (
      <div className="profile-page-root">
        <MLoader />
      </div>
    );
  }

  return (
    <div className="profile-page-root">
      <MHeader
        module="Medications"
        memberInfo={memberInfo}
        onAdd={() =>
          navigate(
            `/profile/health-profile/medication/medicationform?mode=add&type=${
              activeTab === "supplement" ? "supplement" : "medicine"
            }`
          )
        }
        showGradient
        onBack={() => {
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) {
            navigate("/profile/health-profile");
          }
        }}
      />

      <div className="w-full">
        <div className="profile-flex-col-gap">
          <MTabs
            activeTab={activeTab}
            tabs={[
              { value: "medicine", label: t("medications.medicine") },
              { value: "supplement", label: t("medications.supplement") },
            ]}
            onTabChange={(tab) => setActiveTab(tab)}
          />

          <div className="mt-3">
            {activeTab === "medicine" && (
              <>
                {backendMedications.length > 0 ? (
                  <div className="space-y-4">
                    {backendMedications.map((med, idx) => (
                      <MListItem
                        key={med.id}
                        type="2-line"
                        variant="purple"
                        icon={
                          <div className={styles.iconContainer}>
                            <IconPillFilled size={24} color="#8764BB" />
                          </div>
                        }
                        title={
                          med.name + (med.dosage ? ` · ${med.dosage}` : "")
                        }
                        secondaryText={getSecondaryText(med)}
                        actions={[
                          {
                            id: "edit",
                            label: t("common.edit"),
                            icon: <IconEdit size={20} />,
                            onClick: () => handleEdit(med, idx),
                          },
                          {
                            id: "delete",
                            label: t("common.delete"),
                            icon: <IconCircleX size={20} />,
                            onClick: () => {
                              setMedicationToDelete(med);
                              setShowDeleteModal(true);
                            },
                            className: "delete",
                          },
                        ]}
                      />
                    ))}
                  </div>
                ) : (
                  <MEmptyCard
                    icon={<IconPillOff />}
                    primaryTitle={t("medications.noMedicines")}
                    primaryCTA={{
                      text: t("medications.addMedicine"),
                      onClick: () =>
                        navigate(
                          "/profile/health-profile/medication/medicationform?mode=add&type=medicine"
                        ),
                    }}
                  />
                )}
              </>
            )}

            {activeTab === "supplement" && (
              <>
                {backendSupplements.length > 0 ? (
                  <div className="space-y-4">
                    {backendSupplements.map((supplement) => (
                      <MListItem
                        key={supplement.id}
                        type="2-line"
                        variant="purple"
                        icon={
                          <div className={styles.iconContainer}>
                            <IconLeaf size={24} color="#8764BB" />
                          </div>
                        }
                        title={
                          supplement.name +
                          (supplement.dosage ? ` · ${supplement.dosage}` : "")
                        }
                        secondaryText={getSecondaryText(supplement)}
                        actions={[
                          {
                            id: "edit",
                            label: t("common.edit"),
                            icon: <IconEdit size={20} />,
                            onClick: () => {
                              const index = backendSupplements.findIndex(
                                (s) => s.id === supplement.id
                              );
                              navigate(
                                `/profile/health-profile/medication/medicationform?mode=edit&type=supplement&index=${index}`
                              );
                            },
                          },
                          {
                            id: "delete",
                            label: t("common.delete"),
                            icon: <IconCircleX size={20} />,
                            onClick: () => {
                              setSupplementToDelete(supplement);
                              setShowDeleteModal(true);
                            },
                            className: "delete",
                          },
                        ]}
                      />
                    ))}
                  </div>
                ) : (
                  <MEmptyCard
                    icon={<IconLeafOff />}
                    primaryTitle={t("medications.noSupplements")}
                    primaryCTA={{
                      text: t("medications.addSupplement"),
                      onClick: () =>
                        navigate(
                          "/profile/health-profile/medication/medicationform?mode=add&type=supplement"
                        ),
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <MDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMedicationToDelete(null);
          setSupplementToDelete(null);
        }}
        title={
          medicationToDelete
            ? t("medications.deleteMedicine")
            : t("medications.deleteSupplement")
        }
        description={
          medicationToDelete
            ? t(
                `medications.deleteMedicineDescription${
                  memberInfo.onBehalf ? "_on_behalf" : ""
                }`
              )
            : t(
                `medications.deleteSupplementDescription${
                  memberInfo.onBehalf ? "_on_behalf" : ""
                }`
              )
        }
        primaryCtaText={t("common.yes")}
        onPrimaryCta={
          medicationToDelete ? handleDeleteMedication : handleDeleteSupplement
        }
        secondaryCtaText={t("common.no")}
        onSecondaryCta={() => {
          setShowDeleteModal(false);
          setMedicationToDelete(null);
          setSupplementToDelete(null);
        }}
      />
      <EmptyAfterDeleteDialog
        isOpen={showEmptyDialog}
        onConfirm={() => {
          setShowEmptyDialog(false);
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) navigate("/profile/health-profile");
        }}
      />
    </div>
  );
};

export default Medications;
